import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Modal, Button, Form, Badge, Spinner } from "react-bootstrap";
import { connect } from "react-redux";
import { addToCart } from "../redux/actions/cartActions";
import { buddhismAPI, orderAPI } from "../utils/api";
import { Bootpay } from "@bootpay/client-js";
import { templeSound } from "../utils/templeSound";
import { getRealCandlesForTemple, calculateCandlePeriodInfo } from "../utils/realTempleCandles";
import { prayerCheerAPI } from "../utils/prayerCheerAPI";
import {
  FaPray,
  FaFire,
  FaVolumeUp,
  FaVolumeMute,
  FaArrowLeft,
  FaShoppingCart,
  FaCheckCircle,
  FaHeart,
  FaMapMarkerAlt,
  FaExpand,
  FaCompress,
  FaBookOpen,
  FaCamera,
  FaSearchPlus,
  FaCalendarAlt,
  FaClock,
  FaHistory,
} from "react-icons/fa";
import { GiIncense, GiLotus } from "react-icons/gi";
import { MdTempleBuddhist } from "react-icons/md";
import "../assets/styles/virtual-sanctuary.css";

// 비나이다 프리미엄 기간제 불공 아이템
const PLATFORM_PREMIUM_ITEMS = [
  {
    id: "platform-candle-3d",
    name: "인연 소원초 (3일 불공)",
    price: 3000,
    durationDays: 3,
    description: "3일간 가상 불단에 은은한 촛불을 밝혀 일상의 소원과 마음의 안정을 발원합니다.",
    tag: "3일 점등",
    isPlatformItem: true,
  },
  {
    id: "platform-candle-7d",
    name: "칠성 건강기원초 (7일 불공)",
    price: 7000,
    durationDays: 7,
    description: "칠성여래의 가피로 온 가족의 무병장수와 쾌유, 액운 소멸을 발원합니다.",
    tag: "추천 7일",
    isPlatformItem: true,
  },
  {
    id: "platform-candle-100d",
    name: "원만성취 백일초 (100일 불공)",
    price: 30000,
    durationDays: 100,
    description: "수능, 임용, 취업, 사업 번창 등 큰 서원을 세우고 100일간 정성을 다해 밝힙니다.",
    tag: "백일기도",
    isPlatformItem: true,
  },
  {
    id: "platform-lantern-30d",
    name: "황금 연꽃등 (30일 연등 공양)",
    price: 15000,
    durationDays: 30,
    description: "법당 천장 명당자리에 발원자의 이름과 소원표가 달린 연등을 공양합니다.",
    tag: "연등 공양",
    isPlatformItem: true,
  },
];

const PRAYER_MANTRAS = [
  "나무아미타불",
  "관세음보살",
  "마음의 평화와 안온",
  "소원성취 성불하세요",
  "자비와 광명이 함께하길",
  "번뇌가 눈 녹듯 사라집니다",
];

const VirtualSanctuary = ({ addToCart }) => {
  const { templeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 로그인 상태 및 현재 사용자
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const uname = localStorage.getItem("username");
    setIsAuthenticated(!!token);
    if (token && uname) {
      setCurrentUser(uname);
      setFormData((prev) => ({
        ...prev,
        donorName: prev.donorName || uname,
      }));
    }
  }, []);

  // 로그인 체크 헬퍼
  const checkAuth = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      if (
        window.confirm(
          "부처님 전 공양 접수 및 불단 점등을 위해 로그인이 필요합니다.\n로그인 페이지로 이동하시겠습니까?"
        )
      ) {
        navigate("/login", {
          state: { returnUrl: location.pathname },
        });
      }
      return false;
    }
    return true;
  };

  // 실제 사찰 데이터 상태
  const [templeList, setTempleList] = useState([]);
  const [currentTemple, setCurrentTemple] = useState(null);
  const [loading, setLoading] = useState(true);

  // 사운드 & 기도 인터랙션 상태
  const [isBgmOn, setIsBgmOn] = useState(false);
  const [prostrationCount, setProstrationCount] = useState(0);
  const [floatingText, setFloatingText] = useState(null);

  // 대웅전 법당 시점 (view1: 정면 불단 뷰, view2: 석간 뷰)
  const [shrineView, setShrineView] = useState("view1");

  // 불단에 점등된 실제 촛불 목록 (사찰별 실제 DB 주문 및 접수 데이터)
  const [altarCandles, setAltarCandles] = useState([]);

  // 우측 패널 탭 & 선택 아이템
  const [itemTab, setItemTab] = useState("temple");
  const [selectedItem, setSelectedItem] = useState(null);

  // 소원 신청 폼 상태
  const [formData, setFormData] = useState({
    donorName: "",
    birthDate: "",
    category: "가족건강",
    wish: "",
    candleColor: "gold",
  });

  // 상세/성공/방명록 모달 & 관리자 인증 사진 상태
  const [selectedCandleModal, setSelectedCandleModal] = useState(null);
  const [activeAdminImgIndex, setActiveAdminImgIndex] = useState(0);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [showWishesModal, setShowWishesModal] = useState(false);

  // 전체화면 (Fullscreen) 기능 상태
  const hallRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // PG 결제 진행 중 상태
  const [isPaying, setIsPaying] = useState(false);

  // 사찰별 실제 촛불 로드 함수
  const loadCandlesForTemple = (temple) => {
    if (!temple) return;
    const templeKey = temple.id || temple.name;
    const baseCandles = getRealCandlesForTemple(temple);

    // 사용자가 해당 사찰에 추가 점등한 촛불 확인
    const userKey = `sanctuary_user_candles_${templeKey}`;
    let userCandles = [];
    try {
      const saved = localStorage.getItem(userKey);
      if (saved) {
        userCandles = JSON.parse(saved);
      }
    } catch (e) {
      userCandles = [];
    }

    const combined = [...userCandles, ...baseCandles];
    setAltarCandles(combined);

    // DB 실시간 축원(합장) 카운트 동기화
    syncDbCheerCounts(temple.id);
  };

  // AWS RDS MySQL DB 실시간 축원 카운트 동기화 함수
  const syncDbCheerCounts = async (templeId) => {
    try {
      const countsMap = await prayerCheerAPI.getCounts(templeId);
      if (countsMap && Object.keys(countsMap).length > 0) {
        setAltarCandles((prevCandles) =>
          prevCandles.map((c) =>
            countsMap[c.id] !== undefined
              ? { ...c, prayCount: countsMap[c.id] }
              : c
          )
        );
        setSelectedCandleModal((prevModal) =>
          prevModal && countsMap[prevModal.id] !== undefined
            ? { ...prevModal, prayCount: countsMap[prevModal.id] }
            : prevModal
        );
      }
    } catch (e) {
      // 무시 (오류 시 이전 상태 유지)
    }
  };

  useEffect(() => {
    fetchInitialData();

    const handleFullscreenChange = () => {
      const active = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(active);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    // 실시간 DB 축원 카운트 폴링 (4초 주기)
    const cheerInterval = setInterval(() => {
      if (currentTemple?.id) {
        syncDbCheerCounts(currentTemple.id);
      }
    }, 4000);

    return () => {
      templeSound.stopWindChimeBgm();
      clearInterval(cheerInterval);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [templeId, currentTemple?.id]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (hallRef.current) {
        if (hallRef.current.requestFullscreen) {
          hallRef.current.requestFullscreen();
        } else if (hallRef.current.webkitRequestFullscreen) {
          hallRef.current.webkitRequestFullscreen();
        }
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const res = await buddhismAPI.getAll({ size: 20 });
      const list = res.data?.results || res.data || [];
      setTempleList(list);

      if (list.length > 0) {
        let target = null;
        if (templeId) {
          target = list.find((t) => String(t.id) === String(templeId));
          if (!target) {
            try {
              const singleRes = await buddhismAPI.getById(templeId);
              target = singleRes.data;
            } catch (e) {
              target = list[0];
            }
          }
        } else {
          target = list[0];
        }

        setCurrentTemple(target);
        loadCandlesForTemple(target);

        const realItems = extractRealItems(target);
        if (realItems.length > 0) {
          setSelectedItem(realItems[0]);
          setItemTab("temple");
        } else {
          setSelectedItem(PLATFORM_PREMIUM_ITEMS[0]);
          setItemTab("platform");
        }
      }
    } catch (err) {
      console.error("사찰 데이터 로드 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const extractRealItems = (templeObj) => {
    if (!templeObj || !templeObj.item_groups) return [];
    const items = [];
    templeObj.item_groups.forEach((group) => {
      if (group.items && Array.isArray(group.items)) {
        group.items.forEach((item) => {
          items.push({
            ...item,
            groupName: group.name,
            imageUrl: item.images && item.images[0]?.url ? item.images[0].url : null,
          });
        });
      }
    });
    return items;
  };

  const handleSelectTemple = (templeObj) => {
    setCurrentTemple(templeObj);
    navigate(`/sanctuary/${templeObj.id}`, { replace: true });
    loadCandlesForTemple(templeObj);

    const realItems = extractRealItems(templeObj);
    if (realItems.length > 0) {
      setSelectedItem(realItems[0]);
      setItemTab("temple");
    } else {
      setSelectedItem(PLATFORM_PREMIUM_ITEMS[0]);
      setItemTab("platform");
    }
  };

  const handleHitMoktak = () => {
    templeSound.playMoktak();
    setProstrationCount((prev) => prev + 1);

    const randomMantra = PRAYER_MANTRAS[Math.floor(Math.random() * PRAYER_MANTRAS.length)];
    setFloatingText(`${randomMantra}`);
    setTimeout(() => setFloatingText(null), 1400);
  };

  const handleToggleBgm = () => {
    const active = templeSound.toggleBgm();
    setIsBgmOn(active);
  };

  const handleOfferIncense = () => {
    templeSound.playBell(1174.66);
    setFloatingText("청정한 마음으로 부처님 전에 향을 올립니다");
    setTimeout(() => setFloatingText(null), 1500);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLightUpNow = async (e) => {
    e.preventDefault();
    if (!checkAuth()) return;
    if (!selectedItem) {
      alert("공양 아이템을 선택해 주세요.");
      return;
    }
    if (!formData.donorName.trim()) {
      alert("발원자 성함을 입력해 주세요.");
      return;
    }
    if (!formData.wish.trim()) {
      alert("간절한 소원문을 입력해 주세요.");
      return;
    }

    const price = Number(selectedItem.price) || 0;
    const orderTitle = `${currentTemple?.name || "사찰"} - ${selectedItem.name} (${formData.donorName.trim()} 불자)`;
    const orderId = `SANC_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

    setIsPaying(true);

    try {
      // Bootpay PG 결제창 호출
      const response = await Bootpay.requestPayment({
        application_id: "6167c2cc7b5ba4002152c51f",
        price: price,
        order_name: orderTitle,
        order_id: orderId,
        pg: "페이레터",
        method: "card",
        user: {
          username: formData.donorName.trim(),
          phone: "01000000000",
          email: "",
        },
        items: [
          {
            name: `${selectedItem.name} - ${formData.donorName.trim()}`,
            qty: 1,
            id: String(selectedItem.id),
            price: price,
          },
        ],
        extra: {
          open_type: "iframe",
        },
      });

      console.log("Bootpay 결제 응답:", response);

      if (response && response.event === "done") {
        // 결제 완료 -> 부처님 전 종소리 타종
        templeSound.playBell(1046.5);

        // 1. 서버에 주문 생성 시도 (orderAPI.create)
        try {
          await orderAPI.create({
            store: currentTemple?.id || "",
            items: [{ item: selectedItem.id, count: 1, options: [] }],
            date: new Date().toISOString(),
            status: "PAID",
            price: price,
            user_comment: formData.wish.trim(),
            pray_type: formData.category,
            receipt_id: response.data?.receipt_id || "",
          });
        } catch (serverErr) {
          console.warn("서버 주문 기록 안내:", serverErr);
        }

        // 2. 불단 촛불 생성 및 실시간 점등
        const newCandle = {
          id: `user-candle-${Date.now()}`,
          donorName: formData.donorName.trim(),
          birthDate: formData.birthDate.trim(),
          category: formData.category,
          wish: formData.wish.trim(),
          candleColor: formData.candleColor || "gold",
          daysLeft: selectedItem?.durationDays || 30,
          prayCount: 1,
          orderDate: new Date().toISOString().split("T")[0],
          templeName: currentTemple?.name || "사찰",
          itemName: selectedItem?.name || "정성 소원초",
          receiptId: response.data?.receipt_id || "",
        };

        const templeKey = currentTemple?.id || currentTemple?.name;
        const userKey = `sanctuary_user_candles_${templeKey}`;
        let savedList = [];
        try {
          savedList = JSON.parse(localStorage.getItem(userKey) || "[]");
        } catch (err) {
          savedList = [];
        }
        const updatedUserCandles = [newCandle, ...savedList];
        localStorage.setItem(userKey, JSON.stringify(updatedUserCandles));

        // 불단 촛불 목록 앞단에 즉시 추가
        setAltarCandles((prev) => [newCandle, ...prev]);

        setSuccessModal(newCandle);
        setFormData({
          donorName: "",
          birthDate: "",
          category: "가족건강",
          wish: "",
          candleColor: "gold",
        });
      } else if (response && response.event === "cancel") {
        alert("결제가 취소되었습니다. 원하실 때 언제든 부처님 전에 정성을 올리실 수 있습니다.");
      } else if (response && response.event === "error") {
        alert(`결제 처리 중 오류가 발생했습니다: ${response.data?.message || "다시 시도해 주세요."}`);
      }
    } catch (err) {
      console.error("Bootpay 결제창 호출 중 오류:", err);
      alert("결제창을 여는 중 문제가 발생했습니다. 브라우저 팝업/차단 설정을 확인해 주세요.");
    } finally {
      setIsPaying(false);
    }
  };

  const handleAddToCartSubmit = (e) => {
    e.preventDefault();
    if (!checkAuth()) return;
    if (!selectedItem) {
      alert("공양 아이템을 선택해 주세요.");
      return;
    }
    if (!formData.donorName.trim()) {
      alert("발원자 성함을 입력해 주세요.");
      return;
    }

    const cartItem = {
      id: selectedItem.id,
      name: `${currentTemple?.name || "사찰"} - ${selectedItem.name}`,
      price: selectedItem.price,
      quantity: 1,
      templeName: currentTemple?.name || "",
      templeId: currentTemple?.id || "",
      donorName: formData.donorName,
      wish: formData.wish || "가족 평안과 소원성취",
      category: formData.category,
      image: selectedItem.imageUrl || (currentTemple?.images && currentTemple.images[0]?.url) || "",
    };

    addToCart(cartItem, 1);
    alert(`[${selectedItem.name}]이(가) 장바구니에 담겼습니다.`);
  };

  const handleCheerWish = async (candleId, e) => {
    if (e) e.stopPropagation();
    templeSound.playBell(1567.98);

    // 1. 즉각적인 낙관적 UI 업데이트
    setAltarCandles((prev) =>
      prev.map((c) =>
        c.id === candleId ? { ...c, prayCount: (c.prayCount || 0) + 1 } : c
      )
    );
    setSelectedCandleModal((prev) =>
      prev && prev.id === candleId
        ? { ...prev, prayCount: (prev.prayCount || 0) + 1 }
        : prev
    );

    setFloatingText("🙏 부처님 전에 함께 축원(합장)의 공덕을 올렸습니다 (+1)");
    setTimeout(() => setFloatingText(null), 1500);

    // 2. AWS RDS MySQL DB에 실시간 저장
    try {
      const res = await prayerCheerAPI.addCheer(candleId, currentTemple?.id || "");
      if (res && res.cheer_count !== undefined) {
        // DB의 정확한 최신 카운트로 반영
        setAltarCandles((prev) =>
          prev.map((c) =>
            c.id === candleId ? { ...c, prayCount: res.cheer_count } : c
          )
        );
        setSelectedCandleModal((prev) =>
          prev && prev.id === candleId
            ? { ...prev, prayCount: res.cheer_count }
            : prev
        );
      }
    } catch (err) {
      console.error("DB 축원 저장 오류:", err);
    }
  };

  if (loading) {
    return (
      <div className="sanctuary-page-wrap d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
        <Spinner animation="border" style={{ color: "#ffd700", width: "3rem", height: "3rem" }} />
        <p className="mt-3" style={{ color: "#ffd700", fontSize: "1.1rem" }}>
          대웅전 법당으로 들어가는 중입니다...
        </p>
      </div>
    );
  }

  const realTempleItems = extractRealItems(currentTemple);
  const shrineBgImage =
    shrineView === "view1"
      ? "/images/temple-shrine-interior.jpg"
      : "/images/temple-shrine-interior-2.jpg";

  return (
    <div className="sanctuary-page-wrap">
      {/* 기도문 플로팅 텍스트 */}
      {floatingText && <div className="prayer-floating-text">{floatingText}</div>}

      <Container>
        {/* 1. 상단 사찰 정보 및 컨트롤 바 */}
        <div className="sanc-header-banner">
          <div className="sanc-header-main-row">
            {/* 사찰 기본 정보 */}
            <div className="sanc-temple-meta">
              <Button
                variant="outline-warning"
                size="sm"
                className="rounded-circle d-flex align-items-center justify-content-center p-2"
                style={{ width: "36px", height: "36px", borderColor: "rgba(212,175,55,0.6)", color: "#ffd700" }}
                onClick={() => navigate(currentTemple?.id ? `/buddhism/${currentTemple.id}` : "/pray")}
                title="사찰 상세로 이동"
              >
                <FaArrowLeft size={14} />
              </Button>

              {currentTemple?.images && currentTemple.images[0]?.url ? (
                <img
                  src={currentTemple.images[0].url}
                  alt={currentTemple.name}
                  className="sanc-temple-thumb-img"
                />
              ) : (
                <div className="sanc-temple-thumb-empty">
                  <MdTempleBuddhist />
                </div>
              )}

              <div className="sanc-temple-text">
                <h1>
                  <MdTempleBuddhist /> {currentTemple?.name || "사찰 대웅전"}
                  {currentTemple?.sect?.name && (
                    <Badge bg="warning" text="dark" style={{ fontSize: "0.68rem" }}>
                      {currentTemple.sect.name}
                    </Badge>
                  )}
                </h1>
                <p>
                  <FaMapMarkerAlt size={11} className="me-1" />
                  {currentTemple?.address || "고즈넉한 산사"}
                </p>
              </div>
            </div>

            {/* 상단 컨트롤 버튼들 */}
            <div className="sanc-top-ctrls">
              {/* 시점 변경 */}
              <button
                className="sanc-btn-pill"
                onClick={() => setShrineView((v) => (v === "view1" ? "view2" : "view1"))}
                title="법당 시점 변경 (정면 / 석간)"
              >
                <MdTempleBuddhist />
                <span>시점 {shrineView === "view1" ? "1" : "2"}</span>
              </button>

              {/* BGM 토글 */}
              <button
                className={`sanc-btn-pill ${isBgmOn ? "active" : ""}`}
                onClick={handleToggleBgm}
                title="산사 풍경소리 ON/OFF"
              >
                {isBgmOn ? <FaVolumeUp color="#ffd700" /> : <FaVolumeMute />}
                <span>{isBgmOn ? "풍경소리 ON" : "풍경소리 OFF"}</span>
              </button>

              {/* 핵심: 전체화면 토글 버튼 */}
              <button
                className="sanc-btn-fullscreen-trigger"
                onClick={toggleFullscreen}
                title="대웅전 법당 화면 가득 채우기"
              >
                <FaExpand />
                <span>법당 전체화면</span>
              </button>
            </div>
          </div>

          {/* 실제 등록 사찰 빠른 전환 바 */}
          <div className="sanc-temple-pills-row">
            <span style={{ fontSize: "0.78rem", color: "#ffd700", fontWeight: "700", whiteSpace: "nowrap" }}>
              사찰 전환:
            </span>
            {templeList.map((t) => (
              <button
                key={t.id}
                className={`sanc-temple-mini-pill ${currentTemple?.id === t.id ? "active" : ""}`}
                onClick={() => handleSelectTemple(t)}
              >
                <MdTempleBuddhist size={12} />
                <span>{t.name}</span>
                {t.region && <small>({t.region})</small>}
              </button>
            ))}
          </div>
        </div>

        {/* 2. 메인 2열 레이아웃: 좌측(법당 뷰) & 우측(처음부터 무조건 보이는 공양 폼) */}
        <div className="sanc-split-layout">
          {/* [좌측] 웅장한 대웅전 법당 & 부처님 앞 촛대 불단 (전체화면 대상 ref={hallRef}) */}
          <div
            ref={hallRef}
            className={`sanc-hall-frame ${isFullscreen ? "is-fullscreen" : ""}`}
            style={
              isFullscreen
                ? {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100vw",
                    height: "100vh",
                    maxWidth: "100vw",
                    maxHeight: "100vh",
                    zIndex: 999999,
                    margin: 0,
                    borderRadius: 0,
                    border: "none",
                  }
                : {}
            }
          >
            <div
              className="sanc-stage-canvas"
              style={{
                backgroundImage: `url(${shrineBgImage})`,
                height: isFullscreen ? "100vh" : "560px",
                minHeight: isFullscreen ? "100vh" : "560px",
                maxHeight: isFullscreen ? "100vh" : "none",
                backgroundPosition: "center bottom",
              }}
            >
              <div className="sanc-stage-overlay" />

              {/* 우측 상단 배지 (시점 변경 및 전체화면 종료) */}
              <div className="sanc-stage-badges">
                <button
                  className="sanc-badge-pill"
                  onClick={() => setShrineView((v) => (v === "view1" ? "view2" : "view1"))}
                  title="법당 시점 변경"
                >
                  <MdTempleBuddhist size={12} />
                  <span>시점 {shrineView === "view1" ? "1" : "2"}</span>
                </button>

                <button
                  className="sanc-badge-pill"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? "전체화면 종료 (ESC)" : "법당 전체화면"}
                  style={isFullscreen ? { background: "#d4af37", color: "#171008", fontWeight: "bold" } : {}}
                >
                  {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
                  <span>{isFullscreen ? "전체화면 종료" : "전체화면"}</span>
                </button>
              </div>

              {/* 천장 오색 연등 */}
              <div className="sanc-stage-lanterns">
                {[
                  { color: "pink", text: "건강발원" },
                  { color: "gold", text: "소원성취" },
                  { color: "cyan", text: "사업번창" },
                  { color: "red", text: "가정화목" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="sanc-lantern-node"
                    onClick={() => {
                      templeSound.playBell(1318.51);
                      setFloatingText(`[${item.text}] 밝은 등불이 비춥니다`);
                      setTimeout(() => setFloatingText(null), 1400);
                    }}
                  >
                    <div className="sanc-lantern-knot" />
                    <div className={`sanc-lantern-ball ${item.color}`}>
                      <GiLotus size={20} />
                    </div>
                    <div className="sanc-lantern-tag">{item.text}</div>
                  </div>
                ))}
              </div>

              {/* 부처님 바로 앞 실제 불단(제단) 위치에 놓인 촛대 랙 & 향로 */}
              <div
                className="sanc-stage-altar-area"
                style={
                  isFullscreen
                    ? {
                        position: "absolute",
                        bottom: "72px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "90%",
                        maxWidth: "820px",
                        margin: 0,
                      }
                    : {}
                }
              >
                {/* 부처님 앞 정갈한 향로 */}
                <div className="sanc-incense-spot" onClick={handleOfferIncense} title="터치하여 향 올리기">
                  <GiIncense size={17} />
                  <span>부처님 전에 향 올리기</span>
                  <div className="sanc-smoke-puff" />
                </div>

                {/* 실제 불단 위에 황동 촛대 모양으로 가지런히 세워진 실제 신도 촛불 그리드 */}
                <div className="sanc-real-altar-table">
                  <div className="sanc-altar-title-bar">
                    <span>
                      <FaFire className="me-1" /> {currentTemple?.name} 대웅전 불단
                    </span>
                    <span>점등 {altarCandles.length}좌</span>
                  </div>

                  <div className="sanc-candles-dock-row">
                    {altarCandles.map((candle) => {
                      const periodInfo = calculateCandlePeriodInfo(candle);
                      return (
                        <div
                          key={candle.id}
                          className="sanc-candle-stand-item"
                          onClick={() => {
                            setSelectedCandleModal(candle);
                            setActiveAdminImgIndex(0);
                          }}
                          title={`${candle.donorName} 님의 소원 및 관리자 인증사진 보기 (${periodInfo.daysText})`}
                        >
                          {/* 관리자 실시간 인증 사진 배지 */}
                          {candle.adminImages && candle.adminImages.length > 0 && (
                            <div className="candle-camera-badge" title="스님/관리자 점등 인증 사진 등록됨">
                              <FaCamera size={8} />
                            </div>
                          )}
                          <div className="candle-flame-holder">
                            <div className="candle-flame-glow" />
                            <div className="candle-flame-tongue" />
                          </div>
                          <div className={`candle-wax-stick ${candle.candleColor || "gold"}`}>
                            <div className="candle-name-plate">{candle.donorName}</div>
                          </div>
                          <div className="candle-brass-base">
                            <GiLotus size={11} color="#1a1005" />
                          </div>
                          <div className={`candle-days-tag ${periodInfo.isExpired ? "expired" : ""}`}>
                            {periodInfo.shortTagText}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* 법당 하단 목탁 치기 및 인터랙션 컨트롤 */}
            <div
              className="sanc-hall-bottom-ctrls"
              style={
                isFullscreen
                  ? {
                      position: "absolute",
                      bottom: "14px",
                      left: 0,
                      right: 0,
                      background: "transparent",
                      border: "none",
                      justifyContent: "center",
                      gap: "16px",
                      zIndex: 30,
                    }
                  : {}
              }
            >
              <button className="sanc-action-sub-btn" onClick={handleHitMoktak}>
                <FaPray color="#ffd700" size={15} />
                <span>목탁 치기 ({prostrationCount}배)</span>
              </button>

              <button className="sanc-action-sub-btn" onClick={handleOfferIncense}>
                <GiIncense color="#ffd700" size={16} />
                <span>무료 향 올리기</span>
              </button>

              <button
                className="sanc-action-sub-btn"
                onClick={() => setShowWishesModal(true)}
              >
                <FaBookOpen color="#ffd700" size={14} />
                <span>발원록 ({altarCandles.length})</span>
              </button>
            </div>
          </div>

          {/* [우측] 처음부터 무조건 깔끔하게 나오는 공양 신청 카드 */}
          <div className="sanc-form-card">
            <h2 className="sanc-form-card-title">
              <FaFire color="#ffd700" /> 공양 올리기 & 소원 발원
            </h2>
            <div className="sanc-form-card-sub">
              {currentTemple?.name}에 등록된 실제 공양물을 골라 소원을 발원하세요.
            </div>

            {/* 탭: 사찰 실제 등록 상품 vs 플랫폼 프리미엄 초불공 */}
            <div className="sanc-tab-switch-row">
              <button
                type="button"
                className={`sanc-tab-item-btn ${itemTab === "temple" ? "active" : ""}`}
                onClick={() => {
                  setItemTab("temple");
                  if (realTempleItems.length > 0) setSelectedItem(realTempleItems[0]);
                }}
              >
                사찰 등록 공양물 ({realTempleItems.length})
              </button>
              <button
                type="button"
                className={`sanc-tab-item-btn ${itemTab === "platform" ? "active" : ""}`}
                onClick={() => {
                  setItemTab("platform");
                  setSelectedItem(PLATFORM_PREMIUM_ITEMS[0]);
                }}
              >
                프리미엄 초불공
              </button>
            </div>

            {/* 공양물 리스트 (스크롤) */}
            <div className="sanc-items-scroll-box">
              {itemTab === "temple" ? (
                realTempleItems.length > 0 ? (
                  realTempleItems.map((item) => (
                    <div
                      key={item.id}
                      className={`sanc-product-item-card ${selectedItem?.id === item.id ? "selected" : ""}`}
                      onClick={() => setSelectedItem(item)}
                    >
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} className="sanc-product-thumb" />
                      ) : (
                        <div className="sanc-product-thumb-empty">
                          <MdTempleBuddhist />
                        </div>
                      )}
                      <div className="sanc-product-info">
                        <div className="sanc-product-name">{item.name}</div>
                        <div className="sanc-product-desc">{item.description || "사찰 정성 공양물"}</div>
                      </div>
                      <div className="sanc-product-price">
                        {Number(item.price).toLocaleString()}원
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted small">
                    해당 사찰에 등록된 개별 상품이 없습니다.<br />
                    상단 '프리미엄 초불공' 탭을 이용해 주세요.
                  </div>
                )
              ) : (
                PLATFORM_PREMIUM_ITEMS.map((item) => (
                  <div
                    key={item.id}
                    className={`sanc-product-item-card ${selectedItem?.id === item.id ? "selected" : ""}`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="sanc-product-thumb-empty">
                      <FaFire />
                    </div>
                    <div className="sanc-product-info">
                      <div className="d-flex align-items-center gap-2">
                        <span className="sanc-product-name">{item.name}</span>
                        <Badge bg="warning" text="dark" style={{ fontSize: "0.68rem" }}>
                          {item.tag}
                        </Badge>
                      </div>
                      <div className="sanc-product-desc">{item.description}</div>
                    </div>
                    <div className="sanc-product-price">
                      {Number(item.price).toLocaleString()}원
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 소원 작성 입력 폼 */}
            <Form onSubmit={handleLightUpNow} className="sanc-form-section">
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-warning">선택한 공양물</Form.Label>
                <div className="sanc-selected-summary">
                  <span>{selectedItem ? selectedItem.name : "공양물을 선택해 주세요"}</span>
                  <span>{selectedItem ? `${Number(selectedItem.price).toLocaleString()}원` : ""}</span>
                </div>
              </Form.Group>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Form.Group>
                    <Form.Label className="small fw-semibold text-light">발원자 성함</Form.Label>
                    <Form.Control
                      type="text"
                      name="donorName"
                      placeholder="예: 홍길동"
                      value={formData.donorName}
                      onChange={handleInputChange}
                      required
                      style={{ backgroundColor: "#140e0a", color: "#fff", borderColor: "#453424" }}
                    />
                  </Form.Group>
                </div>
                <div className="col-6">
                  <Form.Group>
                    <Form.Label className="small fw-semibold text-light">소원 구분</Form.Label>
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      style={{ backgroundColor: "#140e0a", color: "#fff", borderColor: "#453424" }}
                    >
                      <option value="가족건강">가족건강</option>
                      <option value="학업/합격">학업·합격</option>
                      <option value="사업/재물">사업·재물</option>
                      <option value="인연성취">좋은 인연</option>
                      <option value="소원성취">만사형통</option>
                      <option value="마음평안">마음평안</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-light">소원초 색상 선택</Form.Label>
                <div className="sanc-color-dot-group">
                  {[
                    { id: "gold", color: "#ffd700", name: "황금" },
                    { id: "white", color: "#ffffff", name: "백색" },
                    { id: "pink", color: "#ff85c0", name: "연꽃" },
                    { id: "jade", color: "#5cdbd3", name: "옥색" },
                  ].map((c) => (
                    <div
                      key={c.id}
                      className={`sanc-color-dot-btn ${formData.candleColor === c.id ? "selected" : ""}`}
                      style={{ backgroundColor: c.color }}
                      onClick={() => setFormData((prev) => ({ ...prev, candleColor: c.id }))}
                      title={c.name}
                    />
                  ))}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-light">간절한 소원문 (발원문)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="wish"
                  placeholder="부처님 전에 올리는 간절한 소원을 자유롭게 적어주세요."
                  value={formData.wish}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: "#140e0a", color: "#fff", borderColor: "#453424" }}
                />
              </Form.Group>

              <div className="sanc-cta-grid">
                <Button
                  variant="outline-warning"
                  type="button"
                  onClick={handleAddToCartSubmit}
                  disabled={isPaying}
                  className="sanc-btn-cart"
                >
                  <FaShoppingCart />
                  <span>장바구니 담기</span>
                </Button>
                <Button
                  variant="warning"
                  type="submit"
                  disabled={isPaying}
                  className="sanc-btn-lightup"
                >
                  {isPaying ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      <span>결제창 연결 중...</span>
                    </>
                  ) : (
                    <>
                      <FaFire />
                      <span>불단에 즉시 점등</span>
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Container>

      {/* [모달 1] 촛불 상세 및 관리자 점등 인증 사진 모달 */}
      <Modal
        show={!!selectedCandleModal}
        onHide={() => setSelectedCandleModal(null)}
        size={selectedCandleModal?.adminImages && selectedCandleModal.adminImages.length > 0 ? "lg" : "md"}
        centered
        contentClassName="bg-dark text-light border-warning"
      >
        {selectedCandleModal && (() => {
          const periodInfo = calculateCandlePeriodInfo(selectedCandleModal);
          return (
            <>
              <Modal.Header closeButton closeVariant="white" className="border-secondary">
                <Modal.Title style={{ color: "#ffd700", fontSize: "1.1rem" }}>
                  <FaFire className="me-2" />
                  {selectedCandleModal.donorName} 님의 정성 소원초
                  
                  {/* 상단 접수 일자 강조 배지 (눈에 아주 잘 띄게!) */}
                  {periodInfo.orderDate && (
                    <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: "0.75rem", fontWeight: "700" }}>
                      <FaCalendarAlt className="me-1" /> {periodInfo.orderDate} 접수
                    </Badge>
                  )}

                  {/* 기간 상태 배지 (지남 / 남음) */}
                  <Badge
                    bg={periodInfo.isExpired ? "danger" : "success"}
                    className="ms-1"
                    style={{ fontSize: "0.72rem" }}
                  >
                    {periodInfo.isExpired ? <FaHistory className="me-1" /> : <FaClock className="me-1" />}
                    {periodInfo.statusBadgeText} ({periodInfo.shortTagText})
                  </Badge>

                  {/* 관리자 인증 배지 */}
                  {selectedCandleModal.adminImages && selectedCandleModal.adminImages.length > 0 && (
                    <Badge bg="info" className="ms-1" style={{ fontSize: "0.72rem", color: "#000" }}>
                      <FaCamera className="me-1" /> 관리자 인증
                    </Badge>
                  )}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="py-3">
                {/* 사찰 관리자(스님)가 보낸 실제 불단 점등 인증 사진 섹션 */}
                {selectedCandleModal.adminImages && selectedCandleModal.adminImages.length > 0 && (
                  <div className="admin-photo-container">
                    <div className="admin-photo-header">
                      <span>
                        <FaCamera className="me-2" /> 사찰 관리자(스님) 점등 인증 사진
                      </span>
                      <Badge bg="warning" text="dark">
                        사진 {activeAdminImgIndex + 1} / {selectedCandleModal.adminImages.length}
                      </Badge>
                    </div>

                    {/* 메인 인증 사진 (클릭 시 원본 확대) */}
                    <div
                      className="admin-photo-main-wrap"
                      onClick={() =>
                        setLightboxImg(
                          selectedCandleModal.adminImages[activeAdminImgIndex] ||
                            selectedCandleModal.adminImages[0]
                        )
                      }
                      title="클릭하여 원본 크기로 확대 보기"
                    >
                      <img
                        src={
                          selectedCandleModal.adminImages[activeAdminImgIndex] ||
                          selectedCandleModal.adminImages[0]
                        }
                        alt="사찰 관리자 점등 인증 사진"
                        className="admin-photo-main-img"
                      />
                      <div className="admin-photo-zoom-hint">
                        <FaSearchPlus size={11} /> 클릭 시 원본 확대
                      </div>
                    </div>

                    {/* 여러 장인 경우 썸네일 선택 바 */}
                    {selectedCandleModal.adminImages.length > 1 && (
                      <div className="admin-photo-thumbs-row">
                        {selectedCandleModal.adminImages.map((imgUrl, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`admin-photo-thumb-btn ${
                              activeAdminImgIndex === idx ? "active" : ""
                            }`}
                            onClick={() => setActiveAdminImgIndex(idx)}
                            title={`인증 사진 ${idx + 1} 보기`}
                          >
                            <img src={imgUrl} alt={`인증사진 썸네일 ${idx + 1}`} />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* 스님 축원 말씀 박스 */}
                    <div className="admin-blessing-box">
                      <div className="fw-bold mb-1" style={{ color: "#ffd700" }}>
                        🙏 사찰 주지스님 축원 말씀
                      </div>
                      {selectedCandleModal.adminMessage ||
                        `${currentTemple?.name} 대웅전 부처님 전에 정성을 다하여 소원초를 봉안하고 축원 기도를 봉행하였습니다. 성불하십시오.`}
                    </div>
                  </div>
                )}

                {/* 신도 소원 정보 섹션 */}
                <div className="text-center mt-2">
                  <Badge bg="warning" text="dark" className="mb-3 px-3 py-1">
                    {selectedCandleModal.category} · {selectedCandleModal.itemName || "소원초"}
                  </Badge>

                  <blockquote
                    style={{
                      backgroundColor: "rgba(255,255,255,0.06)",
                      padding: "16px 20px",
                      borderRadius: "12px",
                      lineHeight: "1.6",
                      color: "#f5eedc",
                      borderLeft: "3px solid #ffd700",
                      fontStyle: "italic",
                      textAlign: "left",
                    }}
                  >
                    "{selectedCandleModal.wish}"
                  </blockquote>

                  {/* 접수 정보 & 기간 상태 3열 카드 그리드 (접수일자가 크고 또렷하게 보임!) */}
                  <div className="sanc-modal-info-grid">
                    {/* 1. 접수 일자 카드 */}
                    <div className="sanc-info-box date-box">
                      <div className="sanc-info-label">
                        <FaCalendarAlt color="#ffd700" /> 접수 일자
                      </div>
                      <div className="sanc-info-value highlight-gold">
                        {periodInfo.orderDate}
                      </div>
                      <div className="sanc-info-sub">
                        {periodInfo.isExpired ? `${periodInfo.shortTagText} 신청` : "정성 접수 완료"}
                      </div>
                    </div>

                    {/* 2. 불공 기도 상태 카드 (지남 / 남음 명확화) */}
                    <div className={`sanc-info-box ${periodInfo.isExpired ? "expired-box" : "active-box"}`}>
                      <div className="sanc-info-label">
                        {periodInfo.isExpired ? <FaHistory color="#ff7875" /> : <FaClock color="#5cdbd3" />}
                        기도 진행 상태
                      </div>
                      <div className="sanc-info-value" style={{ color: periodInfo.isExpired ? "#ff7875" : "#5cdbd3" }}>
                        {periodInfo.daysText}
                      </div>
                      <div className="sanc-info-sub">
                        {periodInfo.isExpired ? "대웅전 불공 회향 완료" : "은은하게 점등 중"}
                      </div>
                    </div>

                    {/* 3. 공양 사찰 카드 */}
                    <div className="sanc-info-box">
                      <div className="sanc-info-label">
                        <MdTempleBuddhist color="#ffd700" /> 공양 사찰
                      </div>
                      <div className="sanc-info-value text-warning">
                        {selectedCandleModal.templeName || currentTemple?.name}
                      </div>
                      <div className="sanc-info-sub">
                        대웅전 불단
                      </div>
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className="border-secondary justify-content-between">
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => {
                    handleCheerWish(selectedCandleModal.id);
                    setSelectedCandleModal((prev) => ({
                      ...prev,
                      prayCount: (prev.prayCount || 0) + 1,
                    }));
                  }}
                >
                  <FaPray className="me-1" />
                  함께 축원하기 ({selectedCandleModal.prayCount || 0})
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setSelectedCandleModal(null)}>
                  닫기
                </Button>
              </Modal.Footer>
            </>
          );
        })()}
      </Modal>

      {/* [모달 1-B] 관리자 인증 사진 원본 확대 (Lightbox) 모달 */}
      <Modal
        show={!!lightboxImg}
        onHide={() => setLightboxImg(null)}
        centered
        size="xl"
        contentClassName="bg-black text-light border-warning p-2"
      >
        <Modal.Header closeButton closeVariant="white" className="border-secondary pb-2">
          <Modal.Title style={{ color: "#ffd700", fontSize: "1rem" }}>
            <FaCamera className="me-2" />
            사찰 관리자 점등 인증 사진 (고화질 원본)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 text-center position-relative" style={{ backgroundColor: "#000" }}>
          {lightboxImg && (
            <img
              src={lightboxImg}
              alt="사찰 관리자 점등 인증 사진 원본"
              style={{
                maxWidth: "100%",
                maxHeight: "82vh",
                objectFit: "contain",
                borderRadius: "6px",
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="border-secondary py-2 justify-content-between">
          <small className="text-muted">
            {currentTemple?.name} 관리자 및 스님이 직접 촬영하여 전송한 실제 사진입니다.
          </small>
          <Button variant="warning" size="sm" onClick={() => setLightboxImg(null)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>

      {/* [모달 2] 점등 완료 축하 모달 */}
      <Modal
        show={!!successModal}
        onHide={() => setSuccessModal(null)}
        centered
        contentClassName="bg-dark text-light border-warning text-center p-3"
      >
        <Modal.Body>
          <div style={{ color: "#ffd700", fontSize: "2.8rem" }}>
            <FaCheckCircle />
          </div>
          <h4 style={{ color: "#ffd700", fontWeight: "bold", marginTop: "12px" }}>
            성불하십시오 🙏
          </h4>
          <p className="mt-2" style={{ color: "#d9cbba", lineHeight: "1.6" }}>
            <strong>{successModal?.donorName}</strong> 님의 정성이 담긴{" "}
            <strong>{successModal?.itemName}</strong>가<br />
            <strong>{currentTemple?.name}</strong> 대웅전 불단에 밝혀졌습니다.
          </p>
          <div
            className="my-3 p-3 rounded"
            style={{ backgroundColor: "rgba(212, 175, 55, 0.12)", border: "1px dashed #ffd700" }}
          >
            "{successModal?.wish}"
          </div>
          <small className="text-muted d-block mb-3">
            앞으로 {successModal?.daysLeft}일 동안 대웅전 부처님 앞 불단에서 은은하게 타오릅니다.
          </small>
          <Button
            variant="warning"
            className="px-4 fw-bold"
            onClick={() => setSuccessModal(null)}
            style={{ backgroundColor: "#ffd700", border: "none", color: "#171008" }}
          >
            확인 및 기도 계속하기
          </Button>
        </Modal.Body>
      </Modal>

      {/* [모달 3] 신도 발원문 방명록 모달 */}
      <Modal
        show={showWishesModal}
        onHide={() => setShowWishesModal(false)}
        size="lg"
        centered
        contentClassName="bg-dark text-light border-warning"
      >
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title style={{ color: "#ffd700", fontSize: "1.1rem" }}>
            <FaBookOpen className="me-2" />
            {currentTemple?.name} 신도 정성 발원록
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <div className="row g-3">
            {altarCandles.map((c) => {
              const pInfo = calculateCandlePeriodInfo(c);
              return (
                <div key={c.id} className="col-md-6">
                  <div
                    className="p-3 rounded"
                    style={{ backgroundColor: "#261b12", borderLeft: "3px solid #ffd700", cursor: "pointer" }}
                    onClick={() => {
                      setShowWishesModal(false);
                      setSelectedCandleModal(c);
                      setActiveAdminImgIndex(0);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="d-flex align-items-center gap-2">
                        <strong style={{ color: "#ffd700" }}>{c.donorName} 보체</strong>
                        {c.adminImages && c.adminImages.length > 0 && (
                          <Badge bg="danger" style={{ fontSize: "0.62rem" }}>
                            <FaCamera /> 인증사진
                          </Badge>
                        )}
                      </div>
                      <Badge bg="secondary" style={{ fontSize: "0.7rem" }}>
                        {c.category}
                      </Badge>
                    </div>
                    <p className="mb-2" style={{ color: "#ede2d3", fontSize: "0.85rem", lineHeight: "1.4" }}>
                      {c.wish}
                    </p>
                    <div className="d-flex justify-content-between align-items-center">
                      <small style={{ color: "#ffd700", fontSize: "0.78rem", fontWeight: "600" }}>
                        <FaCalendarAlt className="me-1" />
                        {pInfo.orderDate} ·{" "}
                        <span style={{ color: pInfo.isExpired ? "#ff7875" : "#5cdbd3" }}>
                          {pInfo.shortTagText}
                        </span>
                      </small>
                      <span style={{ color: "#ffd700", fontSize: "0.78rem" }}>
                        <FaHeart size={11} color="#ff5c5c" className="me-1" />
                        합장 ({c.prayCount || 0})
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

const mapDispatchToProps = {
  addToCart,
};

export default connect(null, mapDispatchToProps)(VirtualSanctuary);
